import type { RoomStatus } from "./types";

export function statusLabel(status: RoomStatus): string {
  switch (status) {
    case "UPCOMING":
      return "Upcoming";
    case "ACTIVE":
      return "Active";
    case "COMPLETED":
      return "Completed";
    case "CANCELLED":
      return "Cancelled";
  }
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function shortTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

export function dateAndTime(iso: string): string {
  return `${shortDate(iso)} · ${shortTime(iso)}`;
}

/**
 * Returns a human-readable string for the timing column based on room state:
 *  - UPCOMING: "in 5 days" or "Today" / "Tomorrow"
 *  - ACTIVE:   "running · day 12"
 *  - COMPLETED: "5 days ago" / shortDate
 *  - CANCELLED: shortDate or empty
 */
export function timingText(input: {
  status: RoomStatus;
  startDate: string;
  endDate: string;
  daysUntilStart: number | null;
  daysSinceCompletion: number | null;
}): string {
  switch (input.status) {
    case "UPCOMING": {
      const d = input.daysUntilStart;
      if (d === null) return shortDate(input.startDate);
      if (d <= 0) return "Today";
      if (d === 1) return "Tomorrow";
      if (d <= 14) return `in ${d} days`;
      return shortDate(input.startDate);
    }
    case "ACTIVE": {
      const start = new Date(input.startDate);
      const day =
        Math.floor((Date.now() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      return `running · day ${Math.max(1, day)}`;
    }
    case "COMPLETED": {
      const d = input.daysSinceCompletion;
      if (d === null) return shortDate(input.endDate);
      if (d === 0) return "ended today";
      if (d === 1) return "ended yesterday";
      if (d <= 14) return `${d} days ago`;
      return shortDate(input.endDate);
    }
    case "CANCELLED":
      return shortDate(input.startDate);
  }
}

export function progressLabel(current: number, total: number): string {
  if (total <= 0) return "0/0";
  const safeCurrent = Math.max(0, Math.min(total, current));
  return `${safeCurrent}/${total}`;
}
